import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Search, Pencil, Save, X, Wallet, Building2, Shuffle, HelpCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Panel,
} from "reactflow";
import "reactflow/dist/style.css";
import AdminLayout from "./AdminLayout";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";

// Exchange options
const exchanges = [
  { value: "binance", label: "Binance" },
  { value: "bybit", label: "Bybit" },
  { value: "coinbase", label: "Coinbase" },
  { value: "kraken", label: "Kraken" },
  { value: "okx", label: "OKX" },
  { value: "huobi", label: "Huobi" },
  { value: "kucoin", label: "KuCoin" },
  { value: "gate.io", label: "Gate.io" },
  { value: "bitfinex", label: "Bitfinex" },
  { value: "bitstamp", label: "Bitstamp" },
  { value: "gemini", label: "Gemini" },
  { value: "crypto.com", label: "Crypto.com" },
  { value: "mexc", label: "MEXC" },
  { value: "bitget", label: "Bitget" },
  { value: "htx", label: "HTX" },
  { value: "poloniex", label: "Poloniex" },
  { value: "upbit", label: "Upbit" },
  { value: "bithumb", label: "Bithumb" },
  { value: "uniswap", label: "Uniswap (DEX)" },
  { value: "pancakeswap", label: "PancakeSwap (DEX)" },
  { value: "sushiswap", label: "SushiSwap (DEX)" },
  { value: "curve", label: "Curve (DEX)" },
  { value: "1inch", label: "1inch (DEX)" },
  { value: "dydx", label: "dYdX (DEX)" },
  { value: "gmx", label: "GMX (DEX)" },
  { value: "jupiter", label: "Jupiter (DEX)" },
  { value: "raydium", label: "Raydium (DEX)" },
  { value: "other", label: "Другая" },
];

// Real exchange logos (working URLs)
const exchangeLogos = {
  binance: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/270.png",
  bybit: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/521.png",
  coinbase: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/89.png",
  kraken: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/24.png",
  okx: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/294.png",
  huobi: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/102.png",
  kucoin: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/311.png",
  "gate.io": "https://s2.coinmarketcap.com/static/img/exchanges/64x64/302.png",
  bitfinex: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/37.png",
  bitstamp: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/70.png",
  gemini: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/151.png",
  "crypto.com": "https://s2.coinmarketcap.com/static/img/exchanges/64x64/1149.png",
  mexc: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/544.png",
  bitget: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/513.png",
  htx: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/102.png",
  poloniex: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/43.png",
  upbit: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/351.png",
  bithumb: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/200.png",
  whitebit: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/482.png",
  bingx: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/1110.png",
  uniswap: "https://s2.coinmarketcap.com/static/img/coins/64x64/7083.png",
  pancakeswap: "https://s2.coinmarketcap.com/static/img/coins/64x64/7186.png",
  sushiswap: "https://s2.coinmarketcap.com/static/img/coins/64x64/6758.png",
  curve: "https://s2.coinmarketcap.com/static/img/coins/64x64/6538.png",
  "1inch": "https://s2.coinmarketcap.com/static/img/coins/64x64/8104.png",
  dydx: "https://s2.coinmarketcap.com/static/img/coins/64x64/11156.png",
  gmx: "https://s2.coinmarketcap.com/static/img/coins/64x64/11857.png",
  jupiter: "https://s2.coinmarketcap.com/static/img/coins/64x64/29210.png",
  raydium: "https://s2.coinmarketcap.com/static/img/coins/64x64/8526.png",
  balancer: "https://s2.coinmarketcap.com/static/img/coins/64x64/5728.png",
  traderjoe: "https://s2.coinmarketcap.com/static/img/coins/64x64/11396.png",
  orca: "https://s2.coinmarketcap.com/static/img/coins/64x64/11165.png",
  velodrome: "https://s2.coinmarketcap.com/static/img/coins/64x64/21074.png",
  camelot: "https://s2.coinmarketcap.com/static/img/coins/64x64/23254.png",
  flux4af: "https://s2.coinmarketcap.com/static/img/coins/64x64/3029.png",
};

// Custom nodes
const WalletNode = ({ data }) => {
  const riskColors = {
    low: "border-emerald-500",
    medium: "border-amber-500",
    high: "border-rose-500",
  };
  return (
    <div className={`w-12 h-12 rounded-full bg-white border-2 flex items-center justify-center shadow-md ${riskColors[data.risk_level] || riskColors.low}`}>
      <Wallet className="w-5 h-5 text-slate-600" />
      {data.label && (
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-medium text-slate-700 bg-white px-2 py-0.5 rounded shadow-sm">
          {data.label}
        </div>
      )}
    </div>
  );
};

const ExchangeNode = ({ data }) => {
  const name = (data.label || data.exchange_name || "").toLowerCase();
  const logo = exchangeLogos[name];
  
  return (
    <div className="w-16 h-16 rounded-xl bg-white border-2 border-blue-300 flex items-center justify-center shadow-md overflow-hidden">
      {logo ? (
        <img src={logo} alt={data.label} className="w-10 h-10 object-contain rounded-lg" />
      ) : (
        <Building2 className="w-6 h-6 text-blue-500" />
      )}
      {data.exchange_name && (
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded capitalize">
          {data.exchange_name}
        </div>
      )}
    </div>
  );
};

const MixerNode = ({ data }) => (
  <div className="w-14 h-14 rounded-lg bg-white border-2 border-rose-400 flex items-center justify-center shadow-md">
    <Shuffle className="w-6 h-6 text-rose-500" />
    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-semibold text-rose-700 bg-rose-50 px-2 py-0.5 rounded">
      Mixer
    </div>
  </div>
);

const UnknownNode = ({ data }) => (
  <div className="w-12 h-12 rounded-lg bg-white border-2 border-slate-300 flex items-center justify-center shadow-md">
    <HelpCircle className="w-5 h-5 text-slate-400" />
    {data.label && (
      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs text-slate-600 bg-white px-2 py-0.5 rounded shadow-sm">
        {data.label}
      </div>
    )}
  </div>
);

const nodeTypes = {
  wallet: WalletNode,
  exchange: ExchangeNode,
  mixer: MixerNode,
  unknown: UnknownNode,
};

const AdminRoutes = ({ user }) => {
  const [routes, setRoutes] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    address_id: ""
  });

  // React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  // Node form state
  const [isNodeDialogOpen, setIsNodeDialogOpen] = useState(false);
  const [nodeForm, setNodeForm] = useState({
    node_type: "wallet",
    address: "",
    label: "",
    exchange_name: "",
    risk_level: "low"
  });

  const fetchRoutes = async () => {
    try {
      const [routesRes, addressesRes] = await Promise.all([
        axios.get(`${API}/admin/routes`, { withCredentials: true }),
        axios.get(`${API}/admin/addresses`, { withCredentials: true })
      ]);
      setRoutes(routesRes.data);
      setAddresses(addressesRes.data);
    } catch (error) {
      toast.error("Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes();
  }, []);

  const openCreateDialog = () => {
    setEditingRoute(null);
    setFormData({ name: "", description: "", address_id: "" });
    setIsDialogOpen(true);
  };

  const openEditor = (route) => {
    setEditingRoute(route);
    
    // Convert route nodes to React Flow format
    const flowNodes = (route.nodes || []).map((node, index) => ({
      id: node.node_id,
      type: node.node_type || "unknown",
      position: { x: node.position_x || index * 150, y: node.position_y || 100 },
      data: {
        label: node.label,
        address: node.address,
        exchange_name: node.exchange_name,
        risk_level: node.risk_level,
      },
    }));

    const flowEdges = (route.edges || []).map((edge) => ({
      id: edge.edge_id,
      source: edge.source_node_id,
      target: edge.target_node_id,
      label: edge.amount ? `${edge.amount} ${edge.token}` : "",
      labelStyle: { fontSize: 10, fontFamily: "JetBrains Mono" },
      style: { stroke: "#94A3B8", strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#94A3B8" },
      animated: true,
    }));

    setNodes(flowNodes);
    setEdges(flowEdges);
    setIsEditorOpen(true);
  };

  const handleCreateRoute = async () => {
    try {
      const response = await axios.post(`${API}/admin/routes`, formData, { withCredentials: true });
      toast.success("Маршрут создан");
      setIsDialogOpen(false);
      fetchRoutes();
      // Open editor for new route
      openEditor(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка создания");
    }
  };

  const saveRoute = async () => {
    if (!editingRoute) return;
    
    try {
      const routeNodes = nodes.map(node => ({
        node_id: node.id,
        node_type: node.type,
        address: node.data.address || "",
        label: node.data.label || "",
        exchange_name: node.data.exchange_name || "",
        risk_level: node.data.risk_level || "low",
        position_x: node.position.x,
        position_y: node.position.y,
      }));

      const routeEdges = edges.map(edge => ({
        edge_id: edge.id,
        source_node_id: edge.source,
        target_node_id: edge.target,
        amount: edge.label?.split(" ")[0] || "",
        token: edge.label?.split(" ")[1] || "ETH",
      }));

      await axios.put(
        `${API}/admin/routes/${editingRoute.route_id}`,
        { nodes: routeNodes, edges: routeEdges },
        { withCredentials: true }
      );
      
      toast.success("Маршрут сохранен");
      fetchRoutes();
    } catch (error) {
      toast.error("Ошибка сохранения");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/routes/${deleteId}`, { withCredentials: true });
      toast.success("Маршрут удален");
      setDeleteId(null);
      fetchRoutes();
    } catch (error) {
      toast.error("Ошибка удаления");
    }
  };

  // Add new node
  const addNode = () => {
    const newNode = {
      id: `node_${Date.now()}`,
      type: nodeForm.node_type,
      position: { x: Math.random() * 400 + 100, y: Math.random() * 300 + 100 },
      data: {
        label: nodeForm.label,
        address: nodeForm.address,
        exchange_name: nodeForm.exchange_name,
        risk_level: nodeForm.risk_level,
      },
    };
    setNodes((nds) => [...nds, newNode]);
    setIsNodeDialogOpen(false);
    setNodeForm({ node_type: "wallet", address: "", label: "", exchange_name: "", risk_level: "low" });
  };

  // Connect nodes
  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({
      ...params,
      id: `edge_${Date.now()}`,
      style: { stroke: "#94A3B8", strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#94A3B8" },
      animated: true,
    }, eds)),
    [setEdges]
  );

  const filteredRoutes = routes.filter(route => 
    route.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    route.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Route Editor View
  if (isEditorOpen && editingRoute) {
    return (
      <AdminLayout user={user}>
        <div className="h-[calc(100vh-140px)] flex flex-col" data-testid="route-editor">
          {/* Editor Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-slate-900">{editingRoute.name}</h1>
              <p className="text-sm text-slate-500">{editingRoute.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setIsNodeDialogOpen(true)} data-testid="add-node-btn">
                <Plus className="w-4 h-4 mr-2" />
                Добавить узел
              </Button>
              <Button onClick={saveRoute} className="bg-slate-900 hover:bg-slate-800" data-testid="save-route-btn">
                <Save className="w-4 h-4 mr-2" />
                Сохранить
              </Button>
              <Button variant="ghost" onClick={() => setIsEditorOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* React Flow Canvas */}
          <div className="flex-1 border border-slate-200 rounded-lg overflow-hidden">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              nodeTypes={nodeTypes}
              fitView
              className="bg-slate-50"
              data-testid="flow-canvas"
            >
              <Background color="#CBD5E1" gap={24} size={1} />
              <Controls />
              <MiniMap />
              <Panel position="bottom-left" className="bg-white/90 backdrop-blur-sm border border-slate-200 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-2">Подсказка: Перетащите от одного узла к другому для создания связи</p>
                <div className="flex gap-4 text-xs">
                  <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full border-2 border-emerald-500"></div> Низкий риск</span>
                  <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full border-2 border-amber-500"></div> Средний риск</span>
                  <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full border-2 border-rose-500"></div> Высокий риск</span>
                </div>
              </Panel>
            </ReactFlow>
          </div>

          {/* Add Node Dialog */}
          <Dialog open={isNodeDialogOpen} onOpenChange={setIsNodeDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Добавить узел</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Тип узла</Label>
                  <Select 
                    value={nodeForm.node_type} 
                    onValueChange={(v) => setNodeForm({ ...nodeForm, node_type: v })}
                  >
                    <SelectTrigger data-testid="select-node-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wallet">
                        <span className="flex items-center gap-2"><Wallet className="w-4 h-4" /> Кошелек</span>
                      </SelectItem>
                      <SelectItem value="exchange">
                        <span className="flex items-center gap-2"><Building2 className="w-4 h-4" /> Биржа</span>
                      </SelectItem>
                      <SelectItem value="mixer">
                        <span className="flex items-center gap-2"><Shuffle className="w-4 h-4" /> Миксер</span>
                      </SelectItem>
                      <SelectItem value="unknown">
                        <span className="flex items-center gap-2"><HelpCircle className="w-4 h-4" /> Неизвестный</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {nodeForm.node_type === "exchange" && (
                  <div className="space-y-2">
                    <Label>Биржа</Label>
                    <Select 
                      value={nodeForm.exchange_name} 
                      onValueChange={(v) => setNodeForm({ ...nodeForm, exchange_name: v, label: v })}
                    >
                      <SelectTrigger data-testid="select-exchange">
                        <SelectValue placeholder="Выберите биржу" />
                      </SelectTrigger>
                      <SelectContent>
                        {exchanges.map(ex => (
                          <SelectItem key={ex.value} value={ex.value}>{ex.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Адрес (опционально)</Label>
                  <Input
                    placeholder="0x..."
                    value={nodeForm.address}
                    onChange={(e) => setNodeForm({ ...nodeForm, address: e.target.value })}
                    className="font-mono"
                    data-testid="input-node-address"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Метка</Label>
                  <Input
                    placeholder="Название узла"
                    value={nodeForm.label}
                    onChange={(e) => setNodeForm({ ...nodeForm, label: e.target.value })}
                    data-testid="input-node-label"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Уровень риска</Label>
                  <Select 
                    value={nodeForm.risk_level} 
                    onValueChange={(v) => setNodeForm({ ...nodeForm, risk_level: v })}
                  >
                    <SelectTrigger data-testid="select-node-risk">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Низкий</SelectItem>
                      <SelectItem value="medium">Средний</SelectItem>
                      <SelectItem value="high">Высокий</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsNodeDialogOpen(false)}>
                  Отмена
                </Button>
                <Button onClick={addNode} className="bg-slate-900 hover:bg-slate-800" data-testid="confirm-add-node">
                  Добавить
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </AdminLayout>
    );
  }

  // Routes List View
  return (
    <AdminLayout user={user}>
      <div className="space-y-6" data-testid="admin-routes">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Маршруты</h1>
            <p className="text-slate-500">Визуализация потоков средств</p>
          </div>
          <Button onClick={openCreateDialog} className="bg-slate-900 hover:bg-slate-800" data-testid="add-route-btn">
            <Plus className="w-4 h-4 mr-2" />
            Создать маршрут
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Поиск маршрутов..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-routes"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredRoutes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              {searchQuery ? "Маршруты не найдены" : "Нет маршрутов. Создайте первый."}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRoutes.map((route) => (
              <Card key={route.route_id} className="card-hover" data-testid={`route-${route.route_id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>{route.name}</span>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openEditor(route)}
                        data-testid={`edit-${route.route_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setDeleteId(route.route_id)}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`delete-${route.route_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500 mb-3">
                    {route.description || "Без описания"}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span>{route.nodes?.length || 0} узлов</span>
                    <span>{route.edges?.length || 0} связей</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Создать маршрут</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Название *</Label>
                <Input
                  placeholder="Название маршрута"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="input-route-name"
                />
              </div>

              <div className="space-y-2">
                <Label>Связанный адрес *</Label>
                <Select 
                  value={formData.address_id} 
                  onValueChange={(v) => setFormData({ ...formData, address_id: v })}
                >
                  <SelectTrigger data-testid="select-address">
                    <SelectValue placeholder="Выберите адрес" />
                  </SelectTrigger>
                  <SelectContent>
                    {addresses.map(addr => (
                      <SelectItem key={addr.address_id} value={addr.address_id}>
                        <span className="flex flex-col">
                          <span className="font-medium">{addr.label || "Без метки"}</span>
                          <span className="font-mono text-xs text-slate-500">
                            {addr.address.slice(0, 12)}...
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Описание</Label>
                <Textarea
                  placeholder="Описание маршрута..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="input-route-desc"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Отмена
              </Button>
              <Button 
                onClick={handleCreateRoute} 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={!formData.name || !formData.address_id}
                data-testid="create-route-btn"
              >
                Создать
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Удалить маршрут?</AlertDialogTitle>
              <AlertDialogDescription>
                Это действие нельзя отменить.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Отмена</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="confirm-delete"
              >
                Удалить
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminRoutes;
